/**
 * Verifiziert eine Lizenz-Signatur im Frontend mit Web Crypto API
 * @param {string} licenseKey - Der License-Key
 * @param {string} productCode - Produktcode (z.B. "ZEITAUF-MOBIL")
 * @param {string} licenseType - License-Type (z.B. "PERMANENT")
 * @param {string|null} expiresAt - Ablaufdatum (RFC3339) oder null
 * @param {string} signature - Base64-kodierte Signatur
 * @param {string} publicKeyPem - RSA Public Key (PEM-Format)
 * @returns {Promise<boolean>} - true wenn Signatur gültig
 */
export async function verifyLicenseSignatureFrontend(
  licenseKey,
  productCode,
  licenseType,
  expiresAt,
  signature,
  publicKeyPem
) {
  try {
    // 1. Erstelle zu verifizierende Daten (gleiches Format wie Backend)
    const expiresAtStr = expiresAt || 'PERMANENT';
    const data = `${licenseKey}|${productCode}|${licenseType}|${expiresAtStr}`;
    
    // 2. Hash der Daten (SHA-256)
    const dataBuffer = new TextEncoder().encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    
    // 3. Importiere Public Key
    const publicKey = await importRSAPublicKey(publicKeyPem);
    
    // 4. Dekodiere Signatur (Base64)
    const signatureBuffer = base64ToArrayBuffer(signature);
    
    // 5. Verifiziere Signatur
    const isValid = await crypto.subtle.verify(
      {
        name: 'RSASSA-PKCS1-v1_5',
        hash: 'SHA-256'
      },
      publicKey,
      signatureBuffer,
      hashBuffer
    );
    
    return isValid;
  } catch (error) {
    // Wenn es ein PKCS1_NOT_SUPPORTED Fehler ist, werfe ihn weiter
    // damit das Frontend weiß, dass es auf Backend-Verifizierung zurückgreifen soll
    if (error.message === 'PKCS1_NOT_SUPPORTED') {
      // Keine Fehlermeldung hier - das Frontend wird auf Backend-Verifizierung zurückgreifen
      throw error;
    }
    console.error('[License-Verification] Signatur-Verifizierung fehlgeschlagen:', error);
    return false;
  }
}

/**
 * Importiert RSA Public Key aus PEM-Format (nur SPKI unterstützt)
 * 
 * HINWEIS: PKCS#1 Format wird nicht unterstützt, da Web Crypto API nur SPKI akzeptiert.
 * Für PKCS#1 Keys wird ein spezieller Fehler geworfen, damit das Frontend
 * auf Backend-Verifizierung zurückgreift.
 */
async function importRSAPublicKey(pem) {
  // Entferne PEM-Header/Footer und Whitespace
  const pemHeaderPKCS1 = '-----BEGIN RSA PUBLIC KEY-----';
  // const pemFooterPKCS1 = '-----END RSA PUBLIC KEY-----'; // Wird derzeit nicht verwendet
  const pemHeaderSPKI = '-----BEGIN PUBLIC KEY-----';
  const pemFooterSPKI = '-----END PUBLIC KEY-----';
  
  // Prüfe ob PKCS#1 Format (BEGIN RSA PUBLIC KEY)
  const isPKCS1 = pem.includes(pemHeaderPKCS1);
  
  if (isPKCS1) {
    // PKCS#1 Format wird nicht unterstützt - Backend-Verifizierung erforderlich
    throw new Error('PKCS1_NOT_SUPPORTED');
  }
  
  // SPKI Format - kann direkt importiert werden
  let pemContents = pem
    .replace(pemHeaderSPKI, '')
    .replace(pemFooterSPKI, '')
    .replace(/\s/g, '');
  
  // Ersetze \n Escape-Sequenzen (falls vorhanden)
  pemContents = pemContents.replace(/\\n/g, '');
  
  // Base64 dekodieren
  const binaryDer = base64ToArrayBuffer(pemContents);
  
  try {
    return await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSASSA-PKCS1-v1_5',
        hash: 'SHA-256'
      },
      false,
      ['verify']
    );
  } catch (error) {
    console.error('[License-Verification] SPKI Import fehlgeschlagen:', error);
    throw new Error('PKCS1_NOT_SUPPORTED');
  }
}

// PKCS#1 zu SPKI Konvertierung entfernt - zu fehleranfällig
// Stattdessen wird bei PKCS#1 Format ein Fehler geworfen,
// damit das Frontend auf Backend-Verifizierung zurückgreift

/**
 * Konvertiert Base64-String zu ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

